import EmptyModal from "~/components/EmptyModal/EmptyModal";
import styles from "./ChangeOrderModal.module.css";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Button from "~/components/Button/Button";
import OrderTimeSelector from "../OrderTimeSelector/OrderTimeSelector";
import OrderContext from "~/contexts/OrderContext";
import { useNavigate, useSearchParams } from "@remix-run/react";
import clsx from "clsx";
import { toZonedTime } from "date-fns-tz";

import { urlGen } from "~/helpers/routesHelpers";
import SiteContext from "~/contexts/SiteContext";
import {
  DeliveryInfo,
  OrderFulfillmentType,
  OrderType,
} from "~/entities/orderEntities";
import AddressPicker from "~/components/AddressPicker/AddressPicker";
import OrderTypeSelector from "../OrderTypeSelector/OrderTypeSelector";
import Input from "~/components/Input/Input";
import { formatDay, formatTime } from "~/helpers/dateHelper";
import Api from "~/api/api";
import { getBestMatchingSlot } from "~/helpers/orderHelper";
import { isMenuOpen } from "~/helpers/menuHelpers";
import MenuContext from "~/contexts/MenuContext";
import ThemeContext from "~/contexts/ThemeContext";
import {
  PlaceDetails,
  parsePlaceAddressComponents,
} from "~/helpers/coordsHelpers";
import { resolveDeliveryAddress } from "./resolveDeliveryAddress";

enum Step {
  OrderType,
  TimeSlots,
  Confirmation,
}

enum ConfirmationMessage {
  OrderTypeChange,
  MenuUnavailable,
}

type ChangeOrderModalProps = {
  visible: boolean;
  onClose: (isReset: boolean, isSave: boolean) => void;
};

const ChangeOrderModal: React.FC<ChangeOrderModalProps> = ({
  visible,
  onClose,
}) => {
  const [step, setStep] = useState(Step.OrderType);

  const [confirmationMessage, setConfirmationMessage] =
    useState<ConfirmationMessage | null>(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    orderSource,
    orderType: savedOrderType,
    deliveryInfo: savedDeliveryInfo,
    setOrderTypeInfo,
    orderSummary,
  } = useContext(OrderContext);
  const { menu } = useContext(MenuContext);
  const { orgId, site } = useContext(SiteContext);
  const { template } = useContext(ThemeContext);

  // Filter order types to only the ones for this order source (e.g. table ordering vs online depending on url)
  const orderTypeOptions = useMemo(
    () =>
      site.orderTypes.filter((orderType) => orderSource === orderType.source),
    [site.orderTypes, orderSource],
  );

  // State for storing form values until the user presses save
  const [orderType, setOrderType] = useState<OrderType | undefined>(
    savedOrderType,
  );
  const [requestedReadyTime, setRequestedReadyTime] = useState(
    orderSummary.requestedReadyTime,
  );
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryPlaceDetails, setDeliveryPlaceDetails] =
    useState<PlaceDetails | null>(null);

  // Set form values to saved values whenever saved values change / whenever the modal is reopened
  // Also make sure we aren't on the confirmation page when this happens
  useEffect(() => {
    if (visible) {
      setRequestedReadyTime(orderSummary.requestedReadyTime);
      setOrderType(savedOrderType);
      setStep(Step.OrderType);
      setDeliveryAddress(savedDeliveryInfo?.address.address1 ?? "");
      setDeliveryPlaceDetails(null);
    }
  }, [
    savedOrderType,
    orderSummary.requestedReadyTime,
    visible,
    savedDeliveryInfo,
  ]);

  // Fetch possible times for each order type whenever we open the modal
  const [timesByOrderType, setTimesByOrderType] = useState<
    Record<string, (string | null)[]>
  >({});
  const [asapWaitByOrderType, setAsapWaitByOrderType] = useState<
    Record<string, number | null>
  >({});
  const [timesLoading, setTimesLoading] = useState(true);

  const fetchTimes = useCallback(async () => {
    setTimesLoading(true);
    const res: Record<string, (string | null)[]> = {};
    const waits: Record<string, number | null> = {};
    await Promise.all(
      orderTypeOptions.map(async (x) => {
        const apiRes = await Api.Site.getSiteTimeSlots(orgId, site.id, x.code);
        res[x.code] = apiRes.times;
        waits[x.code] = apiRes.asapWait;
      }),
    );
    setTimesByOrderType(res);
    setAsapWaitByOrderType(waits);
    setTimesLoading(false);
  }, [orderTypeOptions, orgId, site.id]);
  useEffect(() => {
    if (visible) {
      fetchTimes().then();
    }
  }, [visible, fetchTimes]);

  // List of allowed times for selected order type
  const allowedTimes: (string | null)[] =
    (orderType && timesByOrderType[orderType.code]) ?? [];

  // If selected time is not within the allowed times, change it to the closest allowed time
  const actualRequestedTime = !allowedTimes.length
    ? requestedReadyTime
    : getBestMatchingSlot(allowedTimes, requestedReadyTime);

  // If we came from the sites page, and the order type was selected as a filter before selecting site, we can ask user only to confirm their selection instead of choose
  const previouslySelectedOrderType = searchParams.get("orderTypeCode");

  const timeText = useMemo(() => {
    if (!actualRequestedTime) {
      return "Today, ASAP";
    }
    const zoned = toZonedTime(actualRequestedTime, site.timeZone);
    return `${formatDay(zoned)}, ${formatTime(zoned)}`;
  }, [actualRequestedTime, site.timeZone]);

  const handleClose = (isReset: boolean, isSave: boolean) => {
    onClose(isReset, isSave);
    setStep(Step.OrderType);
  };

  const handleSave = async (isConfirm: boolean) => {
    // Unless this is a confirmation save, first check if the order will require resetting
    if (!isConfirm) {
      // Require confirmation / order reset for changing order type (only if an order exists)
      if (orderSummary.id && orderType?.code !== savedOrderType?.code) {
        setStep(Step.Confirmation);
        setConfirmationMessage(ConfirmationMessage.OrderTypeChange);
        return;
      }

      // Require confirmation / order reset for changing to a time that requires a new menu
      // Could potentially be changed to only confirm if order has no lines + only delete lines when resetting instead of entire order
      if (
        orderSummary.id &&
        menu &&
        !isMenuOpen(menu, actualRequestedTime, site.timeZone)
      ) {
        setStep(Step.Confirmation);
        setConfirmationMessage(ConfirmationMessage.MenuUnavailable);
        return;
      }
    }

    // Delivery
    let deliveryInfo: DeliveryInfo | null = null;

    if (orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY) {
      const parsedAddress = deliveryPlaceDetails
        ? parsePlaceAddressComponents(deliveryPlaceDetails)
        : null;

      deliveryInfo = {
        address: resolveDeliveryAddress(
          deliveryAddress,
          parsedAddress,
          savedDeliveryInfo?.address,
        ),
      };
    }

    orderType &&
      setOrderTypeInfo(orderType, actualRequestedTime, deliveryInfo, isConfirm);
    handleClose(isConfirm, true);
  };

  // Styles
  const selectedOrderTimeTileStyles = {
    backgroundColor: "#f6f6f6",
    borderColor: template.global.button.primaryButtonColour,
    color: template.global.button.primaryButtonColour,
  };

  const asapWait = orderType && asapWaitByOrderType[orderType.code];

  const asapNotLoaded =
    !orderType || asapWaitByOrderType[orderType.code] === undefined;

  const scheduleNotLoaded = !orderType || !timesByOrderType[orderType.code];
  const scheduledAvailable = !!allowedTimes.filter((x) => !!x).length;

  // Change the selected step to the default if we are on time slots, but there are no time slots to select (and we have loaded)
  useEffect(() => {
    if (!timesLoading && !scheduledAvailable && step === Step.TimeSlots) {
      setStep(Step.OrderType);
    }
  }, [timesLoading, scheduledAvailable, step]);

  // We won't display the time slots step unless it is loaded AND there are valid slots to select
  const actualStep =
    step === Step.TimeSlots && (timesLoading || !scheduledAvailable)
      ? Step.OrderType
      : step;

  const storeLocation: google.maps.LatLngLiteral | undefined =
    site.siteAddress.latitude && site.siteAddress.longitude
      ? {
          lat: site.siteAddress.latitude,
          lng: site.siteAddress.longitude,
        }
      : undefined;

  return (
    <EmptyModal
      visible={visible}
      onClose={() => handleClose(false, false)}
      modalClassName={styles.modal}
    >
      {actualStep === Step.OrderType && (
        <div className={clsx(styles.content, styles.flexibleHeight)}>
          <div className={styles.sectionTitle}>
            {previouslySelectedOrderType ? "Confirm" : "Select"} Order Type
          </div>
          <OrderTypeSelector
            onChange={setOrderType}
            selectedOrderType={orderType}
            orderTypes={orderTypeOptions}
          />
          <div className={styles.fields}>
            <div className={styles.field}>
              <span className={styles.label}>Selected Store</span>
              <div className={styles.inputWrapper}>
                <Input readOnly value={site.siteName} />
                <div
                  className={styles.inputChangeButton}
                  onClick={() => navigate(urlGen("Sites", null))}
                >
                  Change Store
                </div>
              </div>
            </div>
            {orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY && (
              <div className={styles.field}>
                <span className={styles.label}>
                  Please enter your address for delivery
                </span>
                <AddressPicker
                  address={deliveryAddress}
                  setAddress={(value) => {
                    setDeliveryAddress(value);
                    setDeliveryPlaceDetails(null);
                  }}
                  onPlaceSelect={setDeliveryPlaceDetails}
                  storeLocation={storeLocation}
                />
              </div>
            )}
            <div className={styles.field}>
              <span className={styles.label}>
                Please select day and time for {orderType?.name}
              </span>
              <div className={styles.tiles}>
                <div
                  className={styles.tile}
                  style={
                    !actualRequestedTime
                      ? selectedOrderTimeTileStyles
                      : undefined
                  }
                  onClick={() => setRequestedReadyTime(null)}
                >
                  <div className={styles.tileHeader}>Today, ASAP</div>
                  <div className={styles.tileContent}>
                    {asapWait
                      ? `About ${asapWait} Minutes`
                      : asapWait === 0
                        ? "As Soon As Possible"
                        : asapNotLoaded
                          ? "\xa0"
                          : "Not Available"}
                  </div>
                </div>
                <div
                  className={styles.tile}
                  style={
                    actualRequestedTime
                      ? selectedOrderTimeTileStyles
                      : undefined
                  }
                  onClick={() => setStep(Step.TimeSlots)}
                >
                  <div className={styles.tileHeader}>Schedule</div>
                  <div className={styles.tileContent}>
                    {step === Step.TimeSlots && timesLoading
                      ? "Loading..."
                      : scheduleNotLoaded
                        ? "\xa0"
                        : !scheduledAvailable
                          ? "Not available"
                          : actualRequestedTime
                            ? timeText
                            : "Choose a time"}
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.buttons}>
              <Button onClick={() => handleSave(false)}>Save</Button>
            </div>
          </div>
        </div>
      )}
      {actualStep === Step.TimeSlots && (
        <OrderTimeSelector
          visible
          onSave={(value: Date | null) => {
            setRequestedReadyTime(value !== null ? value.toISOString() : null);
          }}
          onBack={() => {
            setStep(Step.OrderType);
          }}
          requestedReadyTime={actualRequestedTime}
          options={allowedTimes}
        />
      )}
      {actualStep === Step.Confirmation && (
        <div className={styles.content}>
          <div className={styles.sectionTitle}>You have an active order</div>
          <div className={styles.sectionDescription}>
            {confirmationMessage === ConfirmationMessage.OrderTypeChange
              ? `Changing ${
                  !savedOrderType || !orderType
                    ? "order type"
                    : `from ${savedOrderType.name.toLowerCase()} to ${orderType.name.toLowerCase()}`
                } will start a new order, and your current order will be lost. Please confirm this action.`
              : confirmationMessage === ConfirmationMessage.MenuUnavailable
                ? `The current menu ${
                    menu?.name ? `(${menu?.name}) ` : ""
                  }is not available at the selected time, so continuing will reset your order.`
                : "This action will reset your order."}
          </div>
          <div className={styles.buttons}>
            <Button variant="secondary" onClick={() => setStep(Step.OrderType)}>
              Go back
            </Button>
            <Button variant="primary" onClick={() => handleSave(true)}>
              Confirm
            </Button>
          </div>
        </div>
      )}
    </EmptyModal>
  );
};

export default ChangeOrderModal;
